import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface DashboardStats {
  activeJobs: number;
  totalAdapters: number;
  filesStored: number;
  failedJobs24h: number;
}

export const useDashboardStats = () => {
  const [stats, setStats] = useState<DashboardStats>({
    activeJobs: 0,
    totalAdapters: 0,
    filesStored: 0,
    failedJobs24h: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  const loadStats = useCallback(async () => {
    try {
      // Get active/running jobs count
      const { count: activeJobsCount } = await (supabase as any)
        .from('job_runs')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'running');

      // Get total adapters count
      const { count: adaptersCount } = await (supabase as any)
        .from('adapters')
        .select('*', { count: 'exact', head: true });

      // Get files stored count from storage bucket
      const { data: files } = await supabase.storage
        .from('scraped-data')
        .list('', { limit: 1000 });
      
      // Count all files recursively (check subdirectories)
      let totalFiles = 0;
      if (files) {
        for (const item of files) {
          if (item.id === null) {
            // It's a folder, list its contents
            const { data: subFiles } = await supabase.storage
              .from('scraped-data')
              .list(item.name, { limit: 1000 });
            totalFiles += subFiles?.filter(f => f.id !== null).length || 0;
          } else {
            totalFiles++;
          }
        }
      }

      // Get failed jobs in last 24 hours
      const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { count: failedCount } = await (supabase as any)
        .from('job_runs')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'failed')
        .gte('started_at', twentyFourHoursAgo);

      setStats({
        activeJobs: activeJobsCount || 0,
        totalAdapters: adaptersCount || 0,
        filesStored: totalFiles,
        failedJobs24h: failedCount || 0,
      });
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();

    // Subscribe to realtime updates for job_runs
    const channel = supabase
      .channel('dashboard_stats')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'job_runs',
        },
        () => {
          loadStats();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'adapters',
        },
        () => {
          loadStats();
        }
      )
      .subscribe((status) => {
        console.log('Dashboard stats subscription status:', status);
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [loadStats]);

  return { stats, isLoading, refetch: loadStats };
};
